import { isSuccess } from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { QueryClient } from '@tanstack/react-query';
import {
  getAiAgentPaymentRequestSuggestQueryKey,
  getProcessOcrInvoiceInferencesQueryKey,
  useAiAgentPaymentRequestSuggest,
  useProcessOcrInvoiceInferences,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';

export const useOcrInvoiceInferences = ({
  file,
  invoiceId,
  is_used_ai_suggestion,
}: {
  file?: File;
  invoiceId?: string;
  is_used_ai_suggestion?: boolean;
} = {}) => {
  const isPaymentRequestAiSuggestionFlag = useBooleanFlagValue(
    'payment-request-ai-suggestion',
    false
  );
  const isPaymentRequestAiSuggestionFlag2 = useBooleanFlagValue(
    'payment-request-ai-suggestion2',
    false
  );
  // ai suggest + ff enabled
  const newAiSuggestion = !!(
    is_used_ai_suggestion && isPaymentRequestAiSuggestionFlag2
  );
  const result1 = useOcrOrAiAgentInvoiceInferences({
    enabled: !newAiSuggestion,
    file,
    invoiceId,
    is_used_ai_suggestion,
    isPaymentRequestAiSuggestionFlag,
  });
  const result2 = useAiAgentInvoiceInferences({
    enabled: newAiSuggestion,
    file,
  });

  return newAiSuggestion ? result2 : result1;
};

export const useOcrOrAiAgentInvoiceInferences = ({
  enabled,
  file,
  invoiceId,
  is_used_ai_suggestion,
  isPaymentRequestAiSuggestionFlag,
}: {
  enabled?: boolean;
  file?: File;
  invoiceId?: string;
  is_used_ai_suggestion?: boolean;
  isPaymentRequestAiSuggestionFlag?: boolean;
} = {}) => {
  const { t } = useTranslation();
  const requestBody = useMemo(
    () => ({
      file,
      invoice_id: invoiceId ?? undefined,
      is_used_ai_suggestion: is_used_ai_suggestion ?? undefined,
    }),
    [file, invoiceId, is_used_ai_suggestion]
  );

  const query = useProcessOcrInvoiceInferences(requestBody, {
    query: {
      enabled: !!enabled && !!file,
      staleTime: 0,
      gcTime: 0,
      retry: false,
    },
    request: {
      timeout: 60 * 1000,
      timeoutMessage: t('ocr_inference_timeout'),
      ...(isPaymentRequestAiSuggestionFlag && {
        headers: {
          agentApplicantResource: 'ocr_invoice_inference',
        },
      }),
    },
  });

  const apiError = useMemo(() => {
    if (query.data && !isSuccess(query.data.status)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return query.data.data as Error;
    }
    return null;
  }, [query.data]);

  const error = apiError || query.error;

  const removeQueryCache = useCallback(
    (queryClient: QueryClient) => {
      const queryKey = getProcessOcrInvoiceInferencesQueryKey(requestBody);
      queryClient.removeQueries({ queryKey });
    },
    [requestBody]
  );

  return {
    data: apiError ? undefined : query.data?.data,
    error,
    isLoading: query.isLoading,
    removeQueryCache,
  };
};

export const useAiAgentInvoiceInferences = ({
  file,
  enabled,
}: {
  file?: File;
  enabled?: boolean;
} = {}) => {
  const { t } = useTranslation();

  const requestBody = useMemo(
    () => ({ files: [{ data: file, filename: file?.name ?? 'invoice.pdf' }] }),
    [file]
  );

  const sec = 1000;
  const minute = 60 * sec * 2;

  const query = useAiAgentPaymentRequestSuggest(requestBody, {
    query: {
      enabled: !!enabled && !!file,
      staleTime: 0,
      gcTime: 0,
      retry: false,
    },
    request: {
      timeout: minute,
      timeoutMessage: t('ocr_inference_timeout'),
    },
  });

  const apiError = useMemo(() => {
    if (query.data && !isSuccess(query.data.status)) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      return query.data.data as Error;
    }
    return null;
  }, [query.data]);

  const error = apiError || query.error;
  const removeQueryCache = useCallback(
    (queryClient: QueryClient) => {
      const queryKey = getAiAgentPaymentRequestSuggestQueryKey(requestBody);
      queryClient.removeQueries({ queryKey });
    },
    [requestBody]
  );

  return {
    data: apiError ? undefined : query.data?.data,
    error,
    isLoading: query.isLoading,
    removeQueryCache,
  };
};
